import { useMemo, useState } from 'react';
import { format, isEqual } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import {
  useAuthUserRestaurant,
  useGetRestaurantsReportQuery,
} from '@durma-soft/gros-sdk';

import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';

import { PrintReportButton } from '@/components/shared/print-report-button';
import { LastUpdatedInfo } from '@/components/shared/last-updated-info';
import { DataTable, DataTableHTML } from '@/components/shared/data-table';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';

import print from '@/lib/print/print';

import { columns } from '@/config/restaurant/reports/restaurants';
import { Skeleton } from '@/components/shared/shadcn-ui/skeleton';

const RestaurantReport = () => {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<SortingState>([]);
  const memoizedColumns = useMemo(() => columns(t), [t]);

  const { from, to, setDateRange } = useDatePickerRangeState();

  const restaurant = useAuthUserRestaurant();

  const { data, isLoading, isSuccess, isRefetching, dataUpdatedAt, refetch } =
    useGetRestaurantsReportQuery({
      restaurant_id: restaurant.id,
      start_date: from,
      end_date: to,
    });

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <>
      <Helmet>
        <title>{t('reports.restaurants.title')}</title>
      </Helmet>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold md:text-2xl">
          {t('reports.restaurants.title')}
        </h1>
        {isLoading && <Skeleton className="h-10 w-52" />}
        {isSuccess && (
          <LastUpdatedInfo
            dataUpdatedAt={dataUpdatedAt}
            isRefetching={isRefetching}
            handleRefetch={handleRefetch}
          />
        )}
      </div>
      <div className="flex flex-col lg:flex-row justify-between mt-5 mb-5 gap-3">
        <DatePickerWithRange
          date={{ from: new Date(from), to: new Date(to) }}
          setDate={([from, to]) => setDateRange({ from, to })}
        />
        <PrintReportButton
          disabled={isLoading}
          className="self-start"
          onPrint={(fontScale) =>
            print({
              fontScale,
              content: (
                <div>
                  <div>
                    <div className="flex-between">
                      <h1>{t('reports.restaurants.title')} </h1>
                      <div className="flex-row">
                        <p>{t('common.data-updated-at')}</p>
                        <span>&nbsp;</span>
                        <p>
                          <span> {format(dataUpdatedAt, 'dd.MM.yyyy')}</span>
                          <span> {t('common.in')} </span>
                          <span> {format(dataUpdatedAt, 'HH:mm:ss')}</span>
                        </p>
                      </div>
                    </div>
                    <p>
                      {format(from, 'dd.MM.yyyy')}
                      {!isEqual(from, to) && ` - ${format(to, 'dd.MM.yyyy')}`}
                    </p>
                  </div>
                  <DataTableHTML
                    columns={memoizedColumns}
                    data={data}
                    sorting={sorting}
                    setSorting={setSorting}
                  />
                </div>
              ),
            })
          }
        />
      </div>
      <div className="flex flex-col gap-3">
        <DataTable
          columns={memoizedColumns}
          data={data}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
        />
      </div>
    </>
  );
};

export default RestaurantReport;
